import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import R from 'ramda';
import classNames from 'classnames';
import Breadcrumbs from '@cotyorg/ccx-molecules/Breadcrumbs';
import { CardoCarousel } from '@cotyorg/ccx-organism-carousel';
import Heading from '@cotyorg/ccx-atoms/Heading';
import ProductRating from '@cotyorg/ccx-molecules/ProductRating';
import { trackCarouselClickEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/carousel';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import { ProductGalleryThumbnails } from '@cotyorg/ccx-organism-product-gallery';
import ProductSteps from '../../molecules/ProductSteps';
import BVProductRating from '../../molecules/BVProductRating';
import ProductInfo from '../../organisms/ProductInfo';
import { isSmallBreakpoint, showBV } from '../../utils';
import StandardLayout from '../../templates/StandardLayout';
import { schema } from './helpers';
import ShadeFinder from '../../organisms/ShadeFinder/components/ShadeFinder';

const generateContainerClassNames = (isSmall) =>
  classNames('grid-container', {
    full: isSmall,
  });

const shouldShowSteps = ({ productSteps }) =>
  R.path(['mainImage', 'imageSrc'], productSteps) &&
  R.path(['secondImage', 'imageSrc'], productSteps) &&
  R.path(['thirdImage', 'imageSrc'], productSteps) &&
  R.path(['bgImage', 'imageSrc'], productSteps) &&
  R.path(['steps', 'length'], productSteps) > 0;

const ProductComponent = ({
  sectionHeadings,
  productDetails,
  cardo,
  productSteps,
  breadcrumb,
  currentBreakpoint,
  categoryTitle,
  subcategoryTitle,
  productSwatch,
  tracking,
  retailers,
  locale,
  hidePrice,
  presentationLocale,
  currentUrl,
  shadeFinder,
  enableShadeFinder,
  productSlug,
  selectedSKU,
}) => {
  const isShadeFinderEnabled = Boolean(enableShadeFinder && shadeFinder);

  return (
    <StandardLayout>
      <Breadcrumbs items={breadcrumb} />
      <div className="product-page">
        <div
          className={generateContainerClassNames(
            isSmallBreakpoint({ currentBreakpoint })
          )}
        >
          <div className="grid-x grid-margin-x">
            {isSmallBreakpoint({ currentBreakpoint }) && (
              <div className="cell xsmall-16">
                <Heading
                  extraClass="h2 product-info__heading product-info__heading--top"
                  level={1}
                >
                  {R.path(['productDetails', 'heading'], sectionHeadings)}
                </Heading>
              </div>
            )}

            <div className="cell xsmall-16 medium-8 large-8">
              <ProductGalleryThumbnails
                headingLevel={3}
                rightChevronIconSrc="/ccx-files/assets/arrow-right.svg"
                leftChevronIconSrc="/ccx-files/assets/arrow-left.svg"
                carouselsStoreDataKey="productGallery"
                onIndexChange={({ type, index, isForward }) =>
                  trackCarouselClickEvent({
                    page: 'product',
                    carousel: 'productGallery',
                    type,
                    index,
                    isForward,
                  })
                }
              />
              {isShadeFinderEnabled && (
                <div className="product-page__section-try-on">
                  <ShadeFinder
                    shadeFinderConfiguration={shadeFinder}
                    vtoActualProductSlug={productSlug}
                    actualProductSku={selectedSKU}
                    onClose={() => {
                      if (window && window.YMK) {
                        window.YMK.closeEngine();

                        window.YMK = null;
                      }
                    }}
                  />
                </div>
              )}
            </div>

            <div className="cell xsmall-16 medium-8 large-8">
              <ProductInfo
                sectionHeadings={sectionHeadings}
                productDetails={productDetails}
                currentBreakpoint={currentBreakpoint}
                categoryTitle={categoryTitle}
                subcategoryTitle={subcategoryTitle}
                productSwatch={productSwatch}
                locale={locale}
                hidePrice={hidePrice}
                selectedSKU={selectedSKU}
              />
            </div>
          </div>
        </div>
      </div>

      {shouldShowSteps({ productSteps }) && (
        <ProductSteps
          currentBreakpoint={currentBreakpoint}
          sectionHeadings={sectionHeadings}
          productSteps={productSteps}
          presentationLocale={presentationLocale}
        />
      )}

      {R.path(['relatedProducts', 'length'], cardo) > 0 && (
        <div className="related-products">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="cell xsmall-16">
                <Heading level={2} extraClass="related-products__heading">
                  {R.path(['relatedProducts', 'heading'], sectionHeadings)}
                </Heading>

                <CardoCarousel
                  doNotLazyLoad={false}
                  cardoProps={{
                    cardoStoreDataKey: 'relatedProducts',
                    isTitleUnderlined: false,
                    hasBackground: false,
                    isSmallTitle: true,
                    fullWidth: false,
                    headingLevel: 5,
                    showStarRating: false,
                    fixedHeight: true,
                    zoomOnHover: true,
                    hideDescription: true,
                    alignCenter: false,
                    renderCustomRating: ({ cardoIndex }) =>
                      BVProductRating({
                        bvid: R.path(['relatedProducts', cardoIndex, 'bvid'])(
                          cardo
                        ),
                        disableSeo: true,
                      }),
                  }}
                  carouselProps={{
                    carouselsStoreDataKey: 'relatedProducts',
                    headingLevel: 5,
                    rightChevronIconSrc: '/ccx-files/assets/arrow-right.svg',
                    leftChevronIconSrc: '/ccx-files/assets/arrow-left.svg',
                    onIndexChange: ({ type, index, isForward }) =>
                      trackCarouselClickEvent({
                        page: 'product',
                        carousel: 'relatedProducts',
                        type,
                        index,
                        isForward,
                      }),
                  }}
                  cardsPerPageMap={{
                    xsmall: 1,
                    small: 2,
                    medium: 4,
                    large: 4,
                    xlarge: 4,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <OptionalSection
        renderCondition={showBV(locale)}
        section={() => (
          <div className="product-reviews" id="reviews">
            <div className="grid-container">
              <div className="product-reviews__box">
                <Heading
                  level={3}
                  extraClass="product-reviews__heading"
                  data-test="product-reviews-heading"
                >
                  {R.path(['reviews', 'heading'], sectionHeadings)}
                </Heading>

                <ProductRating
                  averageRating={R.prop('averageRating', productDetails)}
                  imgRatingOnSrc={{
                    fullStar: '/ccx-files/assets/star-on.svg',
                    partialStar1: '/ccx-files/assets/partial-star-1.svg',
                    partialStar2: '/ccx-files/assets/partial-star-2.svg',
                    partialStar3: '/ccx-files/assets/partial-star-3.svg',
                    partialStar4: '/ccx-files/assets/partial-star-4.svg',
                    partialStar5: '/ccx-files/assets/partial-star-5.svg',
                    partialStar6: '/ccx-files/assets/partial-star-6.svg',
                    partialStar7: '/ccx-files/assets/partial-star-7.svg',
                    partialStar8: '/ccx-files/assets/partial-star-8.svg',
                    partialStar9: '/ccx-files/assets/partial-star-9.svg',
                  }}
                  imgRatingOffSrc="/ccx-files/assets/star-off.svg"
                />
                <div
                  data-bv-show="reviews"
                  data-bv-productId={R.prop('bvid', productDetails)}
                />
              </div>
            </div>
          </div>
        )}
      />

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            schema({
              sectionHeadings,
              productDetails,
              tracking,
              retailers,
              currentUrl,
            })
          ),
        }}
      />
    </StandardLayout>
  );
};

ProductComponent.propTypes = {
  productDetails: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    shadeSelectLabel: PropTypes.string.isRequired,
    noReviewsLabel: PropTypes.string.isRequired,
    readReviewsLabel: PropTypes.string.isRequired,
    showMoreRetailersLabel: PropTypes.string.isRequired,
    showLessRetailersLabel: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    tabs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
      })
    ),
  }),
  productSteps: PropTypes.shape({
    imageSrc: PropTypes.string,
    imageSrcSet: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        src: PropTypes.string,
      })
    ),
  }),
  sectionHeadings: PropTypes.shape({
    relatedProducts: PropTypes.shape({
      heading: PropTypes.string,
    }),
    productSteps: PropTypes.shape({
      heading: PropTypes.string,
    }),
    retailers: PropTypes.shape({
      heading: PropTypes.string,
    }),
  }),
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      label: PropTypes.string.isRequired,
    })
  ),
  currentBreakpoint: PropTypes.string.isRequired,
  categoryTitle: PropTypes.string,
  subcategoryTitle: PropTypes.string,
  productSwatch: PropTypes.shape({
    swatches: PropTypes.arrayOf(
      PropTypes.shape({
        sku: PropTypes.string,
        itemName: PropTypes.string,
        skuColor: PropTypes.string,
      })
    ),
  }),
  tracking: PropTypes.shape({
    currencyCode: PropTypes.string,
    product: PropTypes.arrayOf(
      PropTypes.shape({
        attributes: PropTypes.shape({
          productUrl: PropTypes.string,
        }),
      })
    ),
  }),
  retailers: PropTypes.arrayOf(
    PropTypes.shape({
      retailersList: PropTypes.arrayOf(
        PropTypes.shape({
          price: PropTypes.string,
          currencySymbol: PropTypes.string,
        })
      ),
    })
  ),
  cardo: PropTypes.objectOf(PropTypes.array),
  hidePrice: PropTypes.bool,
  locale: PropTypes.string,
  presentationLocale: PropTypes.string,
  currentUrl: PropTypes.string.isRequired,
  shadeFinder: PropTypes.object,
  enableShadeFinder: PropTypes.bool,
  productSlug: PropTypes.string,
  selectedSKU: PropTypes.string,
};

export default connect(
  ({
    content,
    breakpoints,
    locale,
    presentationLocale,
    status,
    productSwatch,
  }) => ({
    productDetails: R.prop('productDetails', content),
    cardo: R.prop('cardo', content),
    productSteps: R.prop('productSteps', content),
    retailers: R.prop('retailers', content),
    sectionHeadings: R.prop('sectionHeadings', content),
    breadcrumb: R.prop('breadcrumb', content),
    currentBreakpoint: R.prop('current', breakpoints),
    categoryTitle: R.prop('categoryTitle')(content),
    subcategoryTitle: R.prop('subcategoryTitle')(content),
    productSwatch: R.prop('productSwatch')(content),
    tracking: R.prop('tracking')(content),
    hidePrice: R.prop('hidePrice')(content),
    locale,
    presentationLocale,
    currentUrl: R.prop('currentUrl', status),
    shadeFinder: R.prop('shadeFinder', content),
    enableShadeFinder: R.prop('enableShadeFinder', content),
    shadeFinderQuiz: R.prop('shadeFinderQuiz', content),
    productSlug: R.prop('productURL', content),
    selectedSKU: R.prop('selectedSKU', productSwatch),
  })
)(ProductComponent);
